import {
  Block,
  Grid,
  RowProps,
  Table,
  Spinner,
  Icon,
  IconButton,
} from '@prometheus/components';
import { FC, useCallback, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getDownloadedVocabularyBundlesList } from '@/api/vocabulary-management';
import {
  ModalContext,
  UseModalContext,
  useList,
  useLocalStorage,
} from '@prometheus/hooks';
import { Status } from '@prometheus/types';
import { columns } from './DownloadedBundles.config';
import { VocabularyBundle } from '@/types/vocabulary-management/VocabularyBundle';
import { DownloadedBundlePreviewModal } from '../DownloadedBundlePreviewModal/DownloadedBundlePreviewModal';
import { useInterval } from '@prometheus/hooks';

export const DownloadedBundles: FC = () => {
  const { t } = useTranslation();
  const { openModal, closeModal } = useContext<UseModalContext>(ModalContext);

  const { storage, setProp, saveToStorage }: any = useLocalStorage(
    `vocabulary-management`,
    {
      pageSize: 10,
      sort: null,
    }
  );

  useEffect(() => {
    saveToStorage();
  }, [saveToStorage]);

  const {
    rawData,
    status,
    getEntityList,
    totalElements,
    pageCount,
    pageNumber,
    pageSize,
  } = useList({
    primaryKey: 'id',
    autoLoad: false,
    methods: {
      read: { method: getDownloadedVocabularyBundlesList },
    },
  });

  const fetchData = useCallback(
    (pageNumber, pageSize, sortBy) =>
      getEntityList({ pageNumber, pageSize, sortBy }),
    []
  );

  const reload = () => {
    getEntityList({ pageNumber, pageSize });
  };

  const onRowClick = (row: RowProps<VocabularyBundle>) => {
    openModal(
      () => (
        <DownloadedBundlePreviewModal
          onClose={closeModal}
          bundle={row.original}
        />
      ),
      t('modals.bundle_summary_header'),
      {
        closeOnClickOutside: true,
        onClose: closeModal,
      }
    );
  };

  useInterval(() => {
    if (
      status !== Status.INITIAL &&
      status !== Status.IN_PROGRESS &&
      status !== Status.IN_PROGRESS_RELOAD
    ) {
      reload();
    }
  }, 10000);

  return (
    <>
      <Block>
        <Grid item xs={12} textAlign="right" pr={2} mt={1}>
          <IconButton onClick={reload} size="small" sx={{ fontSize: 16 }}>
            {status === Status.IN_PROGRESS ||
            status === Status.IN_PROGRESS_RELOAD ? (
              <Spinner size={16} />
            ) : (
              <Icon iconName="reload" sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        </Grid>
        <Grid item xs={12} px={2} pb={2}>
          <Table
            columns={columns()}
            data={rawData}
            isLoading={status === Status.IN_PROGRESS}
            // manualSortBy
            // enableSorting
            enablePagination
            manualPagination
            fetchData={fetchData}
            totalElements={totalElements}
            pageCount={pageCount}
            pageNumber={pageNumber}
            onRowClick={onRowClick}
            pageSize={storage?.pageSize}
            setPageSize={size => {
              setProp('pageSize', size);
            }}
            // TODO - uncomment when PRO-2435 is done
            // initialSort={storage?.sort}
            // setSort={sort => {
            //   setProp('sort', sort);
            // }}
          />
        </Grid>
      </Block>
    </>
  );
};
